/* eslint-disable @typescript-eslint/no-explicit-any */
import { useNavigate } from "react-router-dom";

import { formatDate } from "@/utils";
import { Badge, ProgressBar } from "@tremor/react";
import ReusableTable, { type Column } from "../components/ReusableTable";

const OrgsTable = ({
	orgs,
	getOrgs
}: {
	getOrgs: (
		filters?: { from?: string; to?: string }
	) => Promise<any>;
	orgs: any[]; deleteOrg: (id: string) => void
}) => {
	const navigate = useNavigate();
	const columns: Column[] = [
		{
			key: "name",
			header: "Οργανισμός",
			filter: {
				type: "boolean",
				trueLabel: "Ενεργός",
				falseLabel: "Ανενεργός",
				filterValue: (row: any) => row.is_active,
			},
			render: (value: string, row: any) => (
				<div className="flex flex-col gap-1">
					<div className="">{value}</div>
					{row.is_active ? <Badge color="emerald">Ενεργός</Badge> : <Badge color="gray">Ανενεργός</Badge>}
				</div>
			),
		},
		{
			key: "created_at",
			header: "Ημ/νία Εγγραφής",
			filter: { type: "date-range" },
			render: (value: string) =>
				formatDate(value, { simple: true, withTime: false }),
		},
		{
			key: "from_date",
			header: "Ημ/νία έναρξης ενεργής συνδρομής",
			filter: { type: "date-range" },
			render: (value: string) =>
				value ? formatDate(value, { simple: true, withTime: false }) : "-",
		},
		{
			key: "to_date",
			header: "Ημ/νία λήξης ενεργής συνδρομής",
			filter: { type: "date-range" },
			render: (value: string) =>
				value ? formatDate(value, { simple: true, withTime: false }) : "-",
		},
		{
			key: "active_sub_from_date",
			header: "% Ολοκλήρωσης χρόνου ενεργής συνδρομής",
			filter: {
				type: "number-range",
				filterValue: (row: any) => {
					const from = row.from_date;
					const to = row.to_date;
					if (!from || !to) return 0;
					return Math.min(100, Math.max(0, Math.floor(((Date.now() - new Date(from).getTime()) / (new Date(to).getTime() - new Date(from).getTime())) * 100)));
				},
			},
			sortValue: (row: any) => {
				const from = row.from_date;
				const to = row.to_date;
				if (!from || !to) return -1;
				return Math.min(100, Math.max(0, ((Date.now() - new Date(from).getTime()) / (new Date(to).getTime() - new Date(from).getTime())) * 100));
			},
			render: (_value: string, row: any) => {
				const from = row.from_date;
				const to = row.to_date;
				if (!from || !to) return "-";
				const fromMs = new Date(from).getTime();
				const toMs = new Date(to).getTime();
				const nowMs = Date.now();
				const pct = Math.min(100, Math.max(0, ((nowMs - fromMs) / (toMs - fromMs)) * 100));
				return (
					<div className="flex flex-col gap-1 min-w-[80px]">
						<span className="text-xs">{pct.toFixed(2)}%</span>
						<ProgressBar value={pct} color="blue" className="h-1.5" />
					</div>
				);
			},
		},
		{
			key: "user_count",
			header: "Χρήστες",
			render: (value: number, row: any) => `${value && row?.allowed_users ? value + "/" + row.allowed_users : "-"}`,
		},
		{
			key: "package_name",
			header: "Πακέτο συνδρομής",
			filter: { type: "select" },
			render: (value: string) => value ?? "-",
		},
		{
			key: "allowed_credits",
			header: "Credits συνδρομής",
			render: (value: number) => value ?? "-",
		},
		{
			key: "used_credits",
			header: "Υπολειπόμενα credits",
			sortValue: (row: any) => row.allowed_credits != null && row.used_credits != null ? row.allowed_credits - row.used_credits : -1,
			render: (_value: number, row: any) => {
				const allowed = row.allowed_credits;
				const used = row.used_credits;
				if (allowed == null || used == null) return "-";
				return allowed - used;
			},
		},
		{
			key: "used_credits",
			header: "% Χρήσης",
			sortValue: (row: any) => row.allowed_credits == null ? 0 : row.allowed_credits === 0 ? 100 : (row.used_credits / row.allowed_credits) * 100,
			filter: {
				type: "number-range",
				filterValue: (row: any) => {
					const allowed = row.allowed_credits;
					const used = row.used_credits;
					if (allowed == null || used == null) return 0;
					if (allowed === 0) return 100;
					return Math.min(100, Math.max(0, Math.floor((used / allowed) * 100)));
				},
			},
			render: (_value: number, row: any) => {
				const allowed = row.allowed_credits;
				const used = row.used_credits;
				if (allowed == null || used == null) return "-";
				const pct = allowed === 0 ? 100 : Math.min(100, Math.max(0, (used / allowed) * 100));
				const color = pct >= 90 ? "red" : pct >= 60 ? "amber" : "emerald";
				return (
					<div className="flex flex-col gap-1 min-w-[80px]">
						<span className="text-xs">{pct.toFixed(2)}%</span>
						<ProgressBar value={pct} color={color} className="h-1.5" />
					</div>
				);
			},
		},
	];

	const handleRowClick = (org: any) => {
		navigate(`/org/${org.id}`);
	};

	return (
		<ReusableTable
			searchFunction={getOrgs}
			data={orgs}
			columns={columns}
			onRowClick={handleRowClick}
			searchableFields={["name"]}
		/>
	);
};

export default OrgsTable;
